import React, { useState } from "react";
import {
  Alert, FormControl, IconButton, Menu, MenuItem, Paper, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField,
} from "@mui/material";
import { Check, Close, Delete, Edit, MoreVert, VpnKeyOutlined } from "@mui/icons-material";
import {
  TIPOS_ROL,
  SUBTIPOS_COLABORADOR,
  TIPO_COLABORADOR,
  ROL,
  tipoDeRol,
  nombreRol,
  idrolDeSeleccion,
} from "../../domain/staff/roles";
import FilasSkeleton from "../ui/FilasSkeleton";

const encabezadoSx = { fontSize: "16px", fontWeight: 600, color: "#0B0060", fontFamily: "Bricolage Grotesque" };
const celdaSx = { fontFamily: "Bricolage Grotesque", color: "#0b0060" };
const selectSx = { fontFamily: "Bricolage Grotesque", height: "32px", ".MuiSelect-select": { padding: "4px 10px" } };
const COLUMNAS = 5;

/* Tabla del staff con edición en línea. `puedeGestionar` habilita el menú de
 * acciones (sólo el owner); las operaciones las resuelve el padre. */
const TablaMiembros = ({
  miembros,
  cargando,
  total,
  pagina,
  filasPorPagina,
  onCambiarPagina,
  onCambiarFilas,
  busquedaInput,
  onBuscar,
  hayBusqueda,
  puedeGestionar,
  onGuardar,
  onEliminar,
  onPin,
}) => {
  const [menu, setMenu] = useState({ anchor: null, miembro: null });
  const [edicion, setEdicion] = useState(null); // { id, tipo, subtipo, observacion }
  const [guardando, setGuardando] = useState(false);

  const cerrarMenu = () => setMenu({ anchor: null, miembro: null });

  const empezarEdicion = () => {
    const m = menu.miembro;
    const tipo = tipoDeRol(m.idrol);
    setEdicion({
      id: m.id,
      tipo,
      subtipo: tipo === TIPO_COLABORADOR ? m.idrol : null,
      observacion: m.observacion,
    });
    cerrarMenu();
  };

  const guardar = async (miembro) => {
    const idrol = idrolDeSeleccion(edicion.tipo, edicion.subtipo);
    if (!idrol) return;
    setGuardando(true);
    const ok = await onGuardar(miembro, { idrol, observacion: edicion.observacion });
    setGuardando(false);
    if (ok) setEdicion(null);
  };

  const accion = (fn) => () => {
    const m = menu.miembro;
    cerrarMenu();
    fn(m);
  };

  const renderRol = (m) => {
    if (edicion?.id !== m.id) return nombreRol(m.idrol);
    return (
      <>
        <FormControl size="small" fullWidth sx={{ mb: 1 }}>
          <Select
            value={edicion.tipo || ""}
            sx={selectSx}
            onChange={(e) => {
              const tipo = e.target.value;
              setEdicion((ed) => ({
                ...ed,
                tipo,
                subtipo: tipo === TIPO_COLABORADOR ? ROL.COLABORADOR : null,
              }));
            }}
          >
            {TIPOS_ROL.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </Select>
        </FormControl>
        {edicion.tipo === TIPO_COLABORADOR && (
          <FormControl size="small" fullWidth>
            <Select
              value={edicion.subtipo ?? ROL.COLABORADOR}
              sx={selectSx}
              onChange={(e) => setEdicion((ed) => ({ ...ed, subtipo: e.target.value }))}
            >
              {SUBTIPOS_COLABORADOR.map((s) => <MenuItem key={s.idrol} value={s.idrol}>{s.label}</MenuItem>)}
            </Select>
          </FormControl>
        )}
      </>
    );
  };

  const renderObservacion = (m) => {
    if (edicion?.id !== m.id) return m.observacion || "-";
    return (
      <TextField
        size="small"
        fullWidth
        placeholder="Ej: PUERTA1"
        value={edicion.observacion}
        onChange={(e) => setEdicion((ed) => ({ ...ed, observacion: e.target.value }))}
      />
    );
  };

  const renderAcciones = (m) => {
    if (!puedeGestionar) return null;
    if (edicion?.id === m.id) {
      return (
        <div className="d-flex justify-content-end">
          <IconButton onClick={() => guardar(m)} disabled={guardando} sx={{ color: "#5656ff" }} aria-label="Guardar">
            <Check />
          </IconButton>
          <IconButton onClick={() => setEdicion(null)} sx={{ color: "#FF5656" }} aria-label="Cancelar">
            <Close />
          </IconButton>
        </div>
      );
    }
    return (
      <IconButton aria-label="opciones" onClick={(e) => setMenu({ anchor: e.currentTarget, miembro: m })} sx={{ color: "#5656ff" }}>
        <MoreVert />
      </IconButton>
    );
  };

  return (
    <>
      <TextField
        size="small"
        placeholder="Buscar por nombre, email u observación"
        value={busquedaInput}
        onChange={(e) => onBuscar(e.target.value)}
        sx={{ minWidth: { xs: "100%", sm: 320 } }}
      />

      <TableContainer component={Paper} className="mt-3">
        <Table aria-label="staff">
          <TableHead sx={{ backgroundColor: "#E6E6FF" }}>
            <TableRow>
              <TableCell sx={encabezadoSx}>Usuario</TableCell>
              <TableCell sx={encabezadoSx}>Email</TableCell>
              <TableCell sx={{ ...encabezadoSx, textAlign: "center" }}>Rol</TableCell>
              <TableCell sx={encabezadoSx}>Observación</TableCell>
              <TableCell sx={encabezadoSx} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {cargando ? (
              <FilasSkeleton columnas={COLUMNAS} />
            ) : miembros.length > 0 ? (
              miembros.map((m) => (
                <TableRow key={m.id} hover>
                  <TableCell sx={celdaSx}>{m.nombre || "Desconocido"}</TableCell>
                  <TableCell sx={celdaSx}>{m.email || "Desconocido"}</TableCell>
                  <TableCell sx={{ fontFamily: "Bricolage Grotesque", textAlign: "center" }}>{renderRol(m)}</TableCell>
                  <TableCell sx={{ fontFamily: "Bricolage Grotesque" }}>{renderObservacion(m)}</TableCell>
                  <TableCell align="right">{renderAcciones(m)}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={COLUMNAS} align="center" sx={{ py: 4 }}>
                  <Alert severity="info" sx={{ display: "inline-flex", fontFamily: "Bricolage Grotesque" }}>
                    {hayBusqueda
                      ? "No se encontraron miembros que coincidan con la búsqueda"
                      : "Todavía no hay miembros en el staff"}
                  </Alert>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {!cargando && total > 0 && (
          <TablePagination
            component="div"
            className="d-flex justify-content-center"
            rowsPerPageOptions={[5, 10, 25, 50]}
            count={total}
            rowsPerPage={filasPorPagina}
            page={pagina}
            onPageChange={(_, p) => onCambiarPagina(p)}
            onRowsPerPageChange={(e) => onCambiarFilas(parseInt(e.target.value, 10))}
            labelRowsPerPage="Registros p/página"
            showFirstButton
            showLastButton
          />
        )}
      </TableContainer>

      <Menu anchorEl={menu.anchor} open={Boolean(menu.anchor)} onClose={cerrarMenu}>
        <MenuItem onClick={empezarEdicion}>
          <Edit sx={{ mr: "10px", color: "#5656ff" }} /> Editar
        </MenuItem>
        <MenuItem onClick={accion(onPin)}>
          <VpnKeyOutlined sx={{ mr: "10px", color: "#5656ff" }} />
          {menu.miembro?.tienePin ? "Restablecer PIN" : "Asignar PIN"}
        </MenuItem>
        <MenuItem onClick={accion(onEliminar)}>
          <Delete sx={{ mr: "10px", color: "#FF5656" }} /> Eliminar
        </MenuItem>
      </Menu>
    </>
  );
};

export default TablaMiembros;
